import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { ConditionPickerDialog, type ConditionGroup } from './ConditionPickerDialog'
import { areConditionsLocked, checklistItemsOf, subtypeLabel } from './condition-lib'
import type { PropertyEditorProps } from '@/lib/action-modules/registry/subtypes/types'

/** Picks a checklist item (of another checklist/dropdown subtype) this one depends on. */
export function ConditionChecklistItemEditor({ value, onChanged, subtype, allSubtypes }: PropertyEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const [open, setOpen] = useState(false)

  if (areConditionsLocked(allSubtypes)) {
    return <p className="text-sm text-muted-foreground">{t('tabSubtypes.condition.saveFirst')}</p>
  }

  const selectedId = typeof value === 'number' ? value : null

  const itemText = (text: string | null) =>
    text && text.trim().length > 0 ? text : t('tabSubtypes.condition.unnamedItem')

  // Checklist items come from OTHER checklist/dropdown siblings, grouped by their subtype.
  const items = allSubtypes
    .filter(s => s.editorId !== subtype.editorId)
    .flatMap(s =>
      checklistItemsOf(s)
        .filter(i => i.id != null)
        .map(i => ({ id: i.id as number, text: itemText(i.text), parent: subtypeLabel(s, t), parentKey: s.editorId })),
    )

  const groups: ConditionGroup[] = allSubtypes
    .filter(s => s.editorId !== subtype.editorId)
    .map(s => ({
      key: String(s.editorId),
      label: subtypeLabel(s, t),
      options: items.filter(i => i.parentKey === s.editorId).map(i => ({ id: i.id, label: i.text })),
    }))
    .filter(g => g.options.length > 0)

  const selected = items.find(i => i.id === selectedId)
  const triggerLabel =
    selectedId === null
      ? t('tabSubtypes.condition.none')
      : selected
        ? `${selected.parent} · ${selected.text}`
        : t('tabSubtypes.condition.unknown', { id: selectedId })

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-start font-normal"
        onClick={() => setOpen(true)}
      >
        <span className="truncate">{triggerLabel}</span>
      </Button>
      <ConditionPickerDialog
        open={open}
        onOpenChange={setOpen}
        title={t('tabSubtypes.condition.checklistTitle')}
        groups={groups}
        selectedId={selectedId}
        onSelect={id => onChanged(id)}
      />
    </>
  )
}
